import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const BaseUrl = import.meta.env.VITE_BASE_URL;

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface UsersState {
  users: Record<string, unknown>[];
  status: LoadingStatus;
  error: string | null;
  userId: string;
  changes: Record<string, unknown> | null;
  selectedUser: Record<string, unknown> | null;
  drawerOpen: boolean;
  fetchUserStatus: LoadingStatus;
  fetchUserError: string | null;
  createUserStatus: LoadingStatus;
  createUserError: string | null;
  updateUserStatus: LoadingStatus;
  updateUserError: string | null;
  deleteUserStatus: LoadingStatus;
  deleteUserError: string | null;
  // An admin-set password, held only long enough to show the admin what to pass
  // on: the API returns it once and never stores it in the clear.
  temporaryPasswordStatus: LoadingStatus;
  temporaryPasswordError: string | null;
  temporaryPassword: string | null;
  temporaryPasswordFor: string | null;
  deleteConfirmationOpen: boolean;
  userToDelete: string | null;
  filters: { role: string[]; status: string | string[] };
}

// Define the initial state
const initialState: UsersState = {
  users: [],
  status: "idle",
  error: null,

  userId: "",
  changes: null,
  selectedUser: null,
  drawerOpen: false,

  fetchUserStatus: "idle",
  fetchUserError: null,

  createUserStatus: "idle",
  createUserError: null,

  updateUserStatus: "idle",
  updateUserError: null,

  deleteUserStatus: "idle",
  deleteUserError: null,
  deleteConfirmationOpen: false,
  userToDelete: null,

  temporaryPasswordStatus: "idle",
  temporaryPasswordError: null,
  temporaryPassword: null,
  temporaryPasswordFor: null,

  filters: {
    role: [],
    status: "all",
  },
};

// Helper function to create Bearer Auth header
const createBearerAuthHeader = (token: any) => {
  return `Bearer ${token}`;
};

// Define an async thunk for fetching users
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(
      `${BaseUrl}/get-users-by-filters?profileStatuses=active,inactive,pendingActivation`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: createBearerAuthHeader(token),
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch users");
    }

    const data = await response.json();
    return data;
  },
);

export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async (id: any, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(`${BaseUrl}/get-user?id=${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: createBearerAuthHeader(token),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user");
    }

    const data = await response.json();
    return data;
  },
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (user, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(`${BaseUrl}/create-user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: createBearerAuthHeader(token),
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return rejectWithValue(errorText.trim() || "Failed to create user");
    }

    const data = await response.json();
    return data;
  },
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, ...user }: any, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(`${BaseUrl}/update-user?id=${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: createBearerAuthHeader(token),
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return rejectWithValue(errorText.trim() || "Failed to update user");
    }

    const data = await response.json();
    return data;
  },
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id: any, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(`${BaseUrl}/delete-user?id=${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: createBearerAuthHeader(token),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete user");
    }

    const data = await response.json();
    return data;
  },
);

// Set a temporary password for someone else, for when the reset email does not
// arrive. The password comes back in the response, once: the next request for it
// will not be able to read it again.
export const setTemporaryPassword = createAsyncThunk(
  "users/setTemporaryPassword",
  async (
    { userId, password, email }: { userId: string; password?: string; email?: string },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as any;
    const token = state.login.user?.token;

    if (!token) {
      return rejectWithValue("No token found");
    }

    const response = await fetch(`${BaseUrl}/set-temporary-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: createBearerAuthHeader(token),
      },
      body: JSON.stringify({ userId, password: password || undefined }),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      return rejectWithValue(data?.message || "Failed to set a temporary password");
    }

    return { ...(data ?? {}), email: data?.email || email || "" };
  },
);

// Create the users slice
const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    // Define any synchronous actions here if needed
    openDrawer: (state) => {
      state.drawerOpen = true;
      state.deleteUserStatus = "idle";
      state.deleteUserError = null;
    },
    closeDrawer: (state) => {
      state.drawerOpen = false;
      state.selectedUser = null;
      state.changes = null;
      state.userId = "";
      state.fetchUserStatus = "idle";
      state.fetchUserError = null;
      state.createUserStatus = "idle";
      state.createUserError = null;
      state.updateUserStatus = "idle";
      state.updateUserError = null;
      state.deleteUserStatus = "idle";
      state.deleteUserError = null;
    },
    addUser: (state) => {
      state.userId = "";
      state.selectedUser = null;
      state.changes = null;
      state.drawerOpen = true;
      state.fetchUserStatus = "idle";
      state.fetchUserError = null;
      state.createUserStatus = "idle";
      state.createUserError = null;
      state.updateUserStatus = "idle";
      state.updateUserError = null;
      state.deleteUserStatus = "idle";
      state.deleteUserError = null;
    },
    selectUser: (state, action) => {
      state.userId = action.payload;
      state.drawerOpen = true;
      state.fetchUserStatus = "idle";
      state.fetchUserError = null;
      state.createUserStatus = "idle";
      state.createUserError = null;
      state.updateUserStatus = "idle";
      state.updateUserError = null;
      state.deleteUserStatus = "idle";
      state.deleteUserError = null;
    },
    handleUserChange: (state, action) => {
      state.changes = {
        ...state.changes,
        [action.payload.name]: action.payload.value,
      };
    },
    handleProjectChange: (state, action) => {
      state.changes = {
        ...state.changes,
        projects: action.payload,
      };
    },
    handlePermissionsChange: (state, action) => {
      const { permission, value } = action.payload;
      const existingPermissions =
        state.changes?.permissions || state.selectedUser?.permissions || {};
      state.changes = {
        ...(state.changes || {}),
        permissions: {
          ...existingPermissions,
          [permission]: value,
        },
      };
    },
    updateUserPhoto: (state, action) => {
      if (state.selectedUser) {
        state.selectedUser.photo = action.payload;
      }
    },
    clearUserPhoto: (state) => {
      if (state.selectedUser) {
        state.selectedUser.photo = undefined;
      }
    },
    updateUserListPhoto: (state, action) => {
      const { userId, photoUrl } = action.payload;
      const userIndex = state.users.findIndex((user: any) => user.id === userId);
      if (userIndex !== -1) {
        state.users[userIndex].photo = photoUrl;
      }
    },
    clearUserListPhoto: (state, action) => {
      const { userId } = action.payload;
      const userIndex = state.users.findIndex((user: any) => user.id === userId);
      if (userIndex !== -1) {
        state.users[userIndex].photo = undefined;
      }
    },
    closeDeleteConfirmation: (state) => {
      state.deleteConfirmationOpen = false;
      state.userToDelete = null;
    },
    // The password is only ever shown once, so it is dropped as soon as the
    // dialog that shows it closes.
    clearTemporaryPassword: (state) => {
      state.temporaryPassword = null;
      state.temporaryPasswordFor = null;
      state.temporaryPasswordError = null;
      state.temporaryPasswordStatus = "idle";
    },
    changeFilter: (state, action) => {
      (state.filters as Record<string, unknown>)[action.payload.name] = action.payload.value;
    },
    clearFilters: (state) => {
      state.filters = {
        role: [],
        status: ["active"],
      };
    },
    handleClearStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(fetchUserById.pending, (state) => {
        state.fetchUserStatus = "loading";
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.fetchUserStatus = "succeeded";
        state.selectedUser = {
          id: action.payload?.id || "",
          email: action.payload?.email || "",
          firstName: action.payload?.name?.First || "",
          photo: action.payload?.photo || "",
          lastName: action.payload?.name?.Last || "",
          middleName: action.payload?.name?.Middle || "",
          position: action.payload?.position || "",
          role: action.payload?.role || [],
          roleDescription: action.payload?.roleDescription || "",  
          bankAccount: action.payload?.bankAccount || "",
          projects: action.payload?.projects || [],
          phone: action.payload?.phone || "",
          events: action.payload?.events || [],
          permissions: action.payload?.permissions || {},
          profileStatus: action.payload?.profileStatus
            ? action.payload?.profileStatus?.toLowerCase()
            : "",
        };
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.fetchUserStatus = "failed";
        state.fetchUserError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(createUser.pending, (state) => {
        state.createUserStatus = "loading";
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createUserStatus = "succeeded";
        state.changes = null;
        state.status = "idle";
        state.userId = action.payload?.data?.id || "";
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createUserStatus = "failed";
        state.createUserError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(updateUser.pending, (state) => {
        state.updateUserStatus = "loading";
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateUserStatus = "succeeded";
        // Update selectedUser with the returned data to reflect changes immediately
        if (action.payload) {
          state.selectedUser = {
            id: action.payload?.id || state.selectedUser?.id || "",
            email: action.payload?.email || state.selectedUser?.email || "",
            firstName:
              action.payload?.name?.First ||
              state.selectedUser?.firstName ||
              "",
            photo: action.payload?.photo || state.selectedUser?.photo || "",
            lastName:
              action.payload?.name?.Last || state.selectedUser?.lastName || "",
            middleName:
              action.payload?.name?.Middle ||
              state.selectedUser?.middleName ||
              "",
            position:
              action.payload?.position || state.selectedUser?.position || "",
              
            role: action.payload?.role || state.selectedUser?.role || [],
            roleDescription: action.payload?.roleDescription || state.selectedUser?.roleDescription || "",
            bankAccount:
              action.payload?.bankAccount ||
              state.selectedUser?.bankAccount ||
              "",
            projects:
              action.payload?.projects || state.selectedUser?.projects || [],
            phone: action.payload?.phone || state.selectedUser?.phone || "",
            events: action.payload?.events || state.selectedUser?.events || [],
            permissions:
              action.payload?.permissions ||
              state.selectedUser?.permissions ||
              {},
            profileStatus: action.payload?.profileStatus
              ? action.payload?.profileStatus?.toLowerCase()
              : state.selectedUser?.profileStatus || "",
          };
        }
        state.changes = null;
        state.status = "idle";
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateUserStatus = "failed";
        state.updateUserError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(deleteUser.pending, (state) => {
        state.deleteUserStatus = "loading";
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.deleteUserStatus = "succeeded";
        state.status = "idle";
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteUserStatus = "failed";
        state.deleteUserError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(setTemporaryPassword.pending, (state) => {
        state.temporaryPasswordStatus = "loading";
        state.temporaryPasswordError = null;
      })
      .addCase(setTemporaryPassword.fulfilled, (state, action: any) => {
        state.temporaryPasswordStatus = "succeeded";
        state.temporaryPassword = action.payload?.temporaryPassword ?? null;
        state.temporaryPasswordFor = action.payload?.email ?? null;
      })
      .addCase(setTemporaryPassword.rejected, (state, action) => {
        state.temporaryPasswordStatus = "failed";
        state.temporaryPasswordError = (action.payload as string) ?? action.error?.message ?? null;
      });
  },
});

// Export the actions
export const {
  selectUser,
  openDrawer,
  closeDrawer,
  addUser,
  handleUserChange,
  handleProjectChange,
  handlePermissionsChange,
  updateUserPhoto,
  clearUserPhoto,
  updateUserListPhoto,
  clearUserListPhoto,
  changeFilter,
  clearFilters,
  handleClearStatus,
  closeDeleteConfirmation,
  clearTemporaryPassword,
} = usersSlice.actions;

// Export the async thunk and the reducer
export default usersSlice.reducer;
